import { CalendarPositioner } from "@clockwise/web-commons/src/components/calendar";
import { ICalPositionable } from "@clockwise/web-commons/src/components/calendar/calendar-positioner/types";
import { AttendeeAvatar } from "@clockwise/web-commons/src/ui/AttendeeAvatar";
import { useReadCalendarColors } from "@clockwise/web-commons/src/util/CalendarColorsContext";
import { getFullName } from "@clockwise/web-commons/src/util/profile.util";
import classNames from "classnames";
import { DateTime, Interval } from "luxon";
import React, { useMemo } from "react";
import {
  CalendarProfile,
  useCalendarProfilesByCalendarId,
} from "../multi-calendar-select/util/MultiCalendarSelect.util";
import useCurrentDateTime from "../planner-datetime-marker/hooks/useCurrentDateTime";

const getProfileForCalendarId = (profilesForCalendarIds: CalendarProfile[], calendarId: string) =>
  profilesForCalendarIds.find((profile) => profile.primaryCalendar === calendarId);

export const UserColumnHeader = ({
  calendarIds,
  dateTime,
  orgId,
  timeZones,
  minWidthPercent,
  showBorder,
}: {
  calendarIds: string[];
  dateTime: DateTime;
  orgId: string;
  timeZones: Record<string, string | null>;
  minWidthPercent: number;
  showBorder: boolean;
}) => {
  const { calendarProfiles } = useCalendarProfilesByCalendarId(orgId, calendarIds);

  const profiles = useMemo(() => calendarProfiles ?? [], [calendarProfiles]);

  return (
    <div className="cw-flex cw-flex-row cw-bg-default cw-h-full">
      <UserProfiles
        profiles={profiles}
        calendarIds={calendarIds}
        timeZones={timeZones}
        minWidth={minWidthPercent}
        dateTime={dateTime}
        showBorder={showBorder}
      />
    </div>
  );
};

const UserProfiles = ({
  profiles,
  calendarIds,
  timeZones,
  minWidth,
  dateTime,
  showBorder,
}: {
  profiles: CalendarProfile[];
  calendarIds: string[];
  timeZones: Record<string, string | null>;
  minWidth: number;
  dateTime: DateTime;
  showBorder: boolean;
}) => {
  const calendarColors = useReadCalendarColors();
  return (
    <div className="cw-flex cw-w-full cw-h-full cw-relative">
      {calendarIds.map((calendarId, index) => {
        const profile = getProfileForCalendarId(profiles, calendarId);
        const zone = timeZones[calendarId];

        return (
          <UserProfile
            key={calendarId}
            calendarId={calendarId}
            profile={profile}
            zone={zone}
            index={index}
            columnCount={calendarIds.length}
            dateTime={dateTime}
            borderColor={calendarColors[calendarId]?.border}
            minWidth={minWidth}
            showBorder={showBorder}
          />
        );
      })}
    </div>
  );
};

const UserProfile = ({
  profile,
  calendarId,
  zone,
  borderColor,
  dateTime,
  index,
  columnCount,
  minWidth,
  showBorder,
}: {
  calendarId: string;
  zone: string | null;
  borderColor?: string;
  profile?: CalendarProfile;
  dateTime: DateTime;
  index: number;
  columnCount: number;
  minWidth: number;
  showBorder: boolean;
}) => {
  if (!profile) {
    return null;
  }
  const fullName = getFullName(profile.profile);
  const positionables: ICalPositionable[] = [
    {
      key: `${calendarId}-single-day-head`,
      interval: Interval.fromDateTimes(
        dateTime.startOf("day"),
        dateTime.startOf("day").plus({ hours: 2 }),
      ),
      render: () => (
        <div
          className={classNames(
            "cw-flex cw-flex-col cw-flex-1 cw-min-w-0 cw-items-center cw-flex-wrap cw-min-w-[150px]",
            { "cw-border-b cw-border-subtle cw-border-solid": showBorder },
          )}
          key={calendarId}
          style={{ height: "50px" }}
        >
          <div className="cw-flex cw-items-center cw-justify-center cw-flex-wrap">
            <div className="cw-body-base cw-text-14 cw-mr-2">{fullName}</div>
            {profile.profile && (
              <AttendeeAvatar
                size="large"
                profile={profile.profile}
                borderColor={borderColor ?? "#000000"}
              />
            )}
          </div>
          <div className="cw-pb-[6px]">
            <CurrentZonedDateTime zone={zone} />
          </div>
        </div>
      ),
    },
  ];

  return (
    <CalendarPositioner
      dateTimes={[dateTime]}
      positionables={positionables}
      columnIndexOverride={index}
      columnCountOverride={columnCount}
      minWidth={minWidth}
    />
  );
};

const CurrentZonedDateTime = ({ zone }: { zone: string | null }) => {
  const nowDateTime: DateTime = useCurrentDateTime();

  if (!zone) {
    return null;
  }

  const nowZoned = nowDateTime.setZone(zone);
  const zoneAbbreviation = nowZoned.toFormat("ZZZZ");

  return (
    <div className="cw-body-sm cw-text-11 cw-mr-2">{`${nowZoned
      .toFormat("h:mma")
      .toLowerCase()} ${zoneAbbreviation}`}</div>
  );
};
